import React, { useState, useEffect } from 'react';
import { Play, CreditCard, Star, X, Loader2, RefreshCw } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { revenueCat } from '../utils/revenueCat';
import { admobService } from '../utils/admob';
import { crazyGames } from '../utils/crazyGames';
import { HOUSE_PROMO_SECONDS, HouseAd, useHouseAds } from './HouseAd';
import { isAdFreeProduct, REVENUECAT_ENTITLEMENT_ID } from '../config/purchaseProducts';

interface PaywallProps {
  onUnlock: (method: 'ad' | 'sub') => void;
  onClose: () => void;
}

const ADS_REQUIRED = 1;

export const Paywall: React.FC<PaywallProps> = ({ onUnlock, onClose }) => {
  const [packages, setPackages] = useState<PurchasesPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [adCount, setAdCount] = useState(0);
  const [watchingAd, setWatchingAd] = useState(false);
  const [showPromoBreak, setShowPromoBreak] = useState(false);
  const [promoSeconds, setPromoSeconds] = useState(HOUSE_PROMO_SECONDS);
  const campaigns = useHouseAds();
  const isNative = Capacitor.isNativePlatform();
  const isIosNative = isNative && Capacitor.getPlatform() === 'ios';
  const usesRevenueAds = (isNative && !isIosNative) || crazyGames.isAvailable();
  const videoCampaigns = campaigns.filter((campaign) => Boolean(campaign.videoUrl));
  const promotionCampaigns = videoCampaigns.length > 0 ? videoCampaigns : campaigns;
  const [promotionRotationStart] = useState(() => {
    const saved = Number.parseInt(localStorage.getItem('housePromotionRotationStart') || '0', 10);
    return Number.isFinite(saved) && saved >= 0 ? saved : 0;
  });

  useEffect(() => {
    if (isNative) {
      loadOfferings();
    } else {
      setLoading(false);
    }
  }, [isNative]);

  useEffect(() => {
    if (!showPromoBreak || promoSeconds <= 0) return;
    const timer = window.setTimeout(() => setPromoSeconds((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [promoSeconds, showPromoBreak]);

  useEffect(() => {
    if (usesRevenueAds || videoCampaigns.length === 0) return;
    const nextLead = (promotionRotationStart + 1) % videoCampaigns.length;
    localStorage.setItem('housePromotionRotationStart', String(nextLead));
  }, [promotionRotationStart, usesRevenueAds, videoCampaigns.length]);

  const loadOfferings = async () => {
    try {
      const offering = await revenueCat.getOfferings();
      if (offering?.availablePackages) {
        // Token consumables belong in the supply shop, never in the ad-free paywall.
        setPackages(offering.availablePackages.filter((pkg) => isAdFreeProduct(pkg.product.identifier)));
      }
    } catch (e) {
      console.error('Failed to load offerings', e);
    } finally {
      setLoading(false);
    }
  };

  const handleWatchAd = async () => {
    if (watchingAd) return;
    setWatchingAd(true);
    try {
      if (isNative && !isIosNative) {
        await admobService.showRewardVideo();
      } else {
        if (!crazyGames.isAvailable()) {
          setPromoSeconds(HOUSE_PROMO_SECONDS);
          setShowPromoBreak(true);
          return;
        }
        const completed = await crazyGames.requestAd('rewarded');
        if (!completed) return;
      }
      const next = adCount + 1;
      setAdCount(next);
      if (next >= ADS_REQUIRED) {
        onUnlock('ad');
      }
    } catch (e) {
      console.error('Ad failed:', e);
    } finally {
      setWatchingAd(false);
    }
  };

  const completePromoBreak = () => {
    if (promoSeconds > 0) return;
    setShowPromoBreak(false);
    const next = adCount + 1;
    setAdCount(next);
    if (next >= ADS_REQUIRED) onUnlock('ad');
  };

  const handlePurchase = async (pkg: PurchasesPackage) => {
    try {
      setPurchasing(true);
      const customerInfo = await revenueCat.purchasePackage(pkg);
      if (customerInfo.entitlements.active[REVENUECAT_ENTITLEMENT_ID]) {
        onUnlock('sub');
      }
    } catch (e: any) {
      if (e.message !== 'User cancelled') {
        alert('Purchase failed: ' + e.message);
      }
    } finally {
      setPurchasing(false);
    }
  };

  const handleRestore = async () => {
    try {
      setPurchasing(true);
      const customerInfo = await revenueCat.restorePurchases();
      if (customerInfo.entitlements.active[REVENUECAT_ENTITLEMENT_ID]) {
        onUnlock('sub');
      } else {
        alert('No active subscription found.');
      }
    } catch {
      alert('Restore failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const adsLeft = ADS_REQUIRED - adCount;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(2, 6, 23, 0.86)' }}
    >
      <div
        className="bg-slate-900/90 backdrop-blur-md border border-slate-700 rounded-2xl max-w-md w-full p-6 shadow-2xl relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.85), rgba(15,23,42,0.95)), url('https://images.unsplash.com/photo-1661777032455-5582bf3eba07?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white z-10">
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="text-center mb-6 relative z-10">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-500/20 text-yellow-500 mb-3 shadow-lg shadow-yellow-500/10">
            <Star className="w-6 h-6 fill-current" />
          </div>
          <h2 className="text-xl font-bold text-white mb-1">Continue Your Adventure</h2>
          <p className="text-slate-400 text-xs">
            {usesRevenueAds ? 'Watch one ad, or remove ads forever.' : `Complete one ${HOUSE_PROMO_SECONDS}-second house-promotion break to continue.`}
          </p>
        </div>

        <div className="space-y-3 relative z-10">

          {showPromoBreak ? (
            <div className="space-y-3">
              <HouseAd
                variant="card"
                placement="web_rewarded_break"
                campaignIndex={(promotionRotationStart + adCount) % promotionCampaigns.length}
                campaignOverride={promotionCampaigns[(promotionRotationStart + adCount) % promotionCampaigns.length]}
              />
              <button
                onClick={completePromoBreak}
                disabled={promoSeconds > 0}
                className="w-full py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:text-slate-400 text-white font-bold transition-colors"
              >
                {promoSeconds > 0 ? `Continue in ${promoSeconds}` : 'Continue Adventure'}
              </button>
            </div>
          ) : (
          /* Watch Ads Option */
          <button
            onClick={handleWatchAd}
            disabled={watchingAd || adCount >= ADS_REQUIRED}
            className="w-full p-3 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-600 rounded-xl flex items-center justify-between group transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-blue-500/20 text-blue-400 rounded-lg group-hover:scale-110 transition-transform flex-shrink-0">
                {watchingAd
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Play className="w-4 h-4" />}
              </div>
              <div className="text-left">
                <div className="font-bold text-white text-sm">
                  {watchingAd
                    ? 'Loading ad...'
                    : adCount === 0
                      ? (usesRevenueAds ? 'Watch Ads to Play' : 'View House Promotions to Play')
                      : `${usesRevenueAds ? 'Watch Ad' : 'View House Promotion'} ${adCount + 1} of ${ADS_REQUIRED}`}
                </div>
                <div className="text-[10px] text-slate-400">
                  {adsLeft} break{adsLeft !== 1 ? 's' : ''} remaining to unlock this level
                </div>
              </div>
            </div>
            {/* Progress dots */}
            <div className="flex gap-1.5 flex-shrink-0">
              {Array.from({ length: ADS_REQUIRED }).map((_, i) => (
                <div
                  key={i}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    i < adCount ? 'bg-blue-400' : 'bg-slate-600'
                  }`}
                />
              ))}
            </div>
          </button>
          )}

          {/* Subscription packages (native only) */}
          {isNative && !loading && packages.map((pkg) => (
            <button
              key={pkg.identifier}
              onClick={() => handlePurchase(pkg)}
              disabled={purchasing}
              className="w-full p-3 bg-gradient-to-r from-yellow-900/40 to-orange-900/40 hover:from-yellow-800/50 hover:to-orange-800/50 disabled:opacity-50 border border-yellow-600/40 rounded-xl flex items-center justify-between group transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg group-hover:scale-110 transition-transform flex-shrink-0">
                  <CreditCard className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <div className="font-bold text-white text-sm">
                    {pkg.product.title || 'Ad-Free Subscription'}
                  </div>
                  <div className="text-[10px] text-yellow-400/80">
                    No ads · Unlimited levels · Cancel anytime
                  </div>
                </div>
              </div>
              <div className="text-sm font-bold text-yellow-300 flex-shrink-0">
                {pkg.product.priceString}
              </div>
            </button>
          ))}

          {/* Loading offerings spinner */}
          {isNative && loading && (
            <div className="flex justify-center py-3">
              <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
            </div>
          )}

          {/* Restore Purchases */}
          {isNative && !loading && (
            <button
              onClick={handleRestore}
              disabled={purchasing}
              className="w-full text-center text-xs text-slate-500 hover:text-slate-300 pt-1 flex items-center justify-center gap-1 transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Restore Purchases
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
